import { ReactNode } from "react";

import { ChevronRight } from "components/icons/generated";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import useTypedContext from "hooks/useTypedContext";
import { TreeNode } from "utils/tree";
import { SpaceWithParentAccessLevel } from "views/Account/Spaces/types";

import { LEVEL_MARGIN, TREE_GUIDE_OFFSET } from "../../constants";
import { SpacesListContext } from "../Context";
import { getIndentSteps } from "../indent";
import styles from "../styles.module.css";

type SpacesListTreeCellProps = TableSimpleCellProps<TreeNode<SpaceWithParentAccessLevel>> & {
  children: ReactNode;
};

const SpacesListTreeCell = ({ item: node, children, ...cellProps }: SpacesListTreeCellProps) => {
  const { expandedKeys, onToggle } = useTypedContext(SpacesListContext);

  const hasChildren = node.children.length > 0;
  const isExpanded = expandedKeys.has(node.id);
  const level = node.path.length - 1;

  const marginLeft = `calc(${LEVEL_MARGIN} * ${getIndentSteps(node)})`;

  return (
    <TableSimpleCell {...cellProps}>
      {level > 0 && (
        <Box className={styles.guides} aria-hidden>
          {Array.from({ length: level }).map((_, ancestorLevel) => (
            <span
              key={ancestorLevel}
              className={styles.guide}
              style={{
                left: `calc(var(--semantic-spacing-x-large) + ${LEVEL_MARGIN} * ${ancestorLevel} + ${TREE_GUIDE_OFFSET})`,
              }}
            />
          ))}
        </Box>
      )}
      <Box gap="medium" align="center" grow="1" style={{ marginLeft }}>
        {hasChildren && (
          <Box shrink="0">
            <ButtonIcon
              icon={ChevronRight}
              iconRotate={isExpanded ? "90" : undefined}
              aria-expanded={isExpanded}
              onPress={() => onToggle(node.id)}
              variant="ghost"
            >
              {isExpanded ? "Collapse" : "Expand"}
            </ButtonIcon>
          </Box>
        )}
        {children}
      </Box>
    </TableSimpleCell>
  );
};

export default SpacesListTreeCell;
